<!--
    T34: THE SEARCH BOX AND ITS RESULTS PANEL, restyled onto `r13-top-bar.html:142-160`.

    THE FETCHING IS NOT TOUCHED. Every keystroke still fires a request (`watch: inputValue` calls
    `getList()` with no debounce), the `CancelToken` made in `created()` is still never used, the POST
    still goes to `api/assetflow/search` with the same two-key payload, the `ledgers` key still prints
    "P-Cards" and "More than {total_count - 1} found." is still the arithmetic. Only the look changed.

    WHAT CHANGED IN THE MARKUP.
      - The panel is a `.ftd-r13-pop--wide` hanging from the box inside a `.ftd-menuwrap`, and ITS HEAD
        IS THE CLOSE LINK ALONE, which is what the live panel has: it does not repeat the search box
        inside itself.
      - THE GROUP HEADING'S INLINE COLOUR IS GONE. It read `window.COLOUR_PLATE.primary_colour` into a
        `style` attribute on every heading; it is one rule on `.ftd-r13-group` in `_topbar.scss` now,
        and the entity's colour reaches it through `--ftd-primary`. `selected_colour` is still read in
        `created()` and is left where it is.
      - TODAY'S LOADING IS A FAKE RESULT ROW named "Loading..." in a group literally called `loading`
        (`getList()` below, unchanged). It is now DRAWN as a loading line with the foundation's own
        `ftd-loading` and `ftd-spin`, and not as a result. That is a look, not a new state.
      - The eighty-three-line unscoped style block that lived at the foot of this file has moved out,
        all of it, and this component keeps no style block of its own.

    THE EMPTY STATE IS NOT BUILT (Log #374 point 26), and that leaves A FAULT OF TODAY'S SITE OPEN: a
    search with no hits still shows a panel holding nothing but the Close link, because nothing here
    handles empty and nothing was added to. It is recorded, not closed.
-->
<template>
    <span class="ftd-menuwrap ftd-r13-searchwrap">
        <div class="ftd-search">
            <i v-if="!is_mobile" class="bx bx-search-alt"></i>
            <input
                ref="myDropdowninput"
                v-model.trim="inputValue"
                type="search"
                :placeholder="$t('navbar.search.text')"
                :class="{ 'focused': focused }"
                @focus="changeDropdownInputWidth(true)"
                @blur="changeDropdownInputWidth(false)"
                class="dropdownInput" />
        </div>

        <!--
            OPEN IS A CLASS AND NOT A `v-show` (which is what this was). The reference's panel is shown
            by `.is-open` and hidden without it, and the bar's own container query reads that class:
            below 900px of bar it gives the search box up for the search icon UNLESS the panel is open
            (`_topbar.scss`, the ftdbar 900px block). A `v-show` leaves `.is-open` written on an element
            that is `display: none`, so `:has(.ftd-r13-pop.is-open)` matched on every page and the box
            never gave its room up at all -- measured at 390px, where the right-hand group was pushed
            clean off the bar. The condition is `inputValue && apiLoaded`, exactly as it was.
        -->
        <div
            class="ftd-r13-pop ftd-r13-pop--wide ftd-r13-pop--left"
            :class="{'is-open': inputValue && apiLoaded}"
        >
            <div class="ftd-r13-pop__head ftd-r13-pop__head--close">
                <button type="button" class="ftd-link close-search" @click="inputValue = ''">Close</button>
            </div>
            <div class="ftd-r13-pop__body">
                <template v-for="(subItem, key) in grouped_itemList">

                    <div
                        class="ftd-r13-group"
                        v-if="key !== 'loading'"
                        :key="key + '--group'"
                    >
                        {{(key === 'ledgers' ? 'P-Cards' : key) | titleize}}<span class="ftd-r13-group__n">{{ subItem.length }}</span>
                    </div>

                    <div
                        class="ftd-r13-res"
                        :class="{'ftd-r13-res--flat': key === 'results' }"
                        v-if="key !== 'loading'"
                        v-for="item in subItem"
                        :key="item.id+item.type"
                        @click="key !== 'results' ? selectItem(item) : false;"
                    >
                        <img v-if="item.logo" :src="item.logo" class="ftd-r13-res__logo" />

                        <span class="ftd-r13-res__text">
                            <span class="ftd-r13-res__name">{{item.uid && !item.hasOwnProperty('sp_location_info') ? item.uid+': ' : ''}}{{ !item.hasOwnProperty('sp_location_info') ? item.name : '' }}</span>

                            <span
                                class="ftd-r13-res__where search-location-item"
                                v-if="item.hasOwnProperty('sp_location_info') && item.sp_location_info"
                                v-html="getLocationHTML(item.sp_location_info)"></span>

                            <span class="ftd-r13-res__where"
                                v-if="(current_item_type === 'group' && item.hasOwnProperty('customer_name'))"
                            ><a class="search-customer-name"
                                :style="(item.customer_accent_colour !== '' ? ('color:' + item.customer_accent_colour + ' !important') : '')"
                            >{{ item.customer_name }}</a></span>
                        </span>

                    </div>

                    <div v-else class="ftd-loading ftd-r13-loading" :key="item.id+item.type">
                        <span class="ftd-spin"></span><span>{{ item.name | titleize }}</span>
                    </div>

                    <div v-if="key !== 'results' && key !== 'loading' && itemMeta[key]" class="ftd-r13-res__all" :key="key + '--all'" @click="seeAll(key)">
                        {{itemMeta[key].hasOwnProperty('has_more') && itemMeta[key].has_more ? 'More than ' + (itemMeta[key].total_count - 1) + ' found. ':''}}<a class="ftd-link">See All</a>
                    </div>

                    <!-- today's `other-objects-list` wrapper and its guard, folded into the loop's own
                         expression: `v-for` is evaluated before `v-if` in Vue 2, so the guard has to
                         be inside the list and not beside it. -->
                    <div v-for="(related_object, ro_key) in (itemMeta[key] && itemMeta[key].hasOwnProperty('related_objects') && itemMeta[key].related_objects ? itemMeta[key].related_objects : [])"
                         :key="key + '--rel--' + ro_key"
                         class="ftd-r13-res__rel"
                         @click="seeAllOf(key, related_object.object_type, related_object.id)">
                        <a :class="'all-for-' + related_object.object_type"><i class="bx bx-link-external"></i>{{ related_object.message }}</a>
                    </div>

                </template>
            </div>
        </div>
    </span>
</template>

<script>
    import Resource from '@/api/resource';
    const searchResource = new Resource('api/assetflow/search');
    import {getLocationDetails} from "@/views/assetflow/form_data/SpLocationSharedFunctions";

    import { SearchResults } from './DummySearchResults';
    export default {
        name: "SearchDropdown",
        data () {
            return {
                selectedItem: {},
                inputValue: '',
                itemList: [],
                itemMeta: [],
                grouped_itemList: [],
                apiLoaded: false,
                apiUrl: '',
                selected_colour: '',
                current_layout: 'default',
                current_item_type: '',
                focused: false,
                is_mobile: false,
                cancelSource: null
            }
        },
        props:{
            mobile_only: {
                type: Boolean,
                default: false
            }
        },
        mounted () {
            this.getList();
        },
        created() {
            this.cancelSource = axios.CancelToken.source();

            this.is_mobile = this.mobile_only ?? false;
            const pathArray = window.location.pathname.split("/");
            this.current_item_type = pathArray.length > 2 && pathArray[1] !== '' ? pathArray[1] : 'customer';
            this.selected_colour = window.COLOUR_PLATE !== undefined && window.COLOUR_PLATE.hasOwnProperty('primary_colour') && window.COLOUR_PLATE.primary_colour ? COLOUR_PLATE.primary_colour : '';
            this.current_layout = CURENT_LAYOUT ? CURENT_LAYOUT : 'default';
        },
        methods: {
            getLocationHTML(sp_location_info){
                return getLocationDetails(sp_location_info, true, true, false);
            },
            changeDropdownInputWidth(focused){
                if (this.is_mobile || this.inputValue !== '') {
                    return;
                }
                this.focused = focused
            },
            selectItem (theItem) {
                this.selectedItem = theItem;
                this.inputValue = theItem.name;
                this.$emit('on-item-selected', theItem);
            },
            seeAll(item_type){
                // console.log("Should Go To " + item_type + " and Search from " + this.inputValue);
                // console.log(this.current_item_type + "/" + item_type + "?s=" + this.inputValue);

                let extra_params = '';
                if (item_type === 'ledgers'){
                    extra_params = '&c=buyer';
                }
                window.location = encodeURI("/" + this.current_item_type + "/" + item_type + "?s=" + this.inputValue + extra_params);
            },
            seeAllOf(item_type, queue_name, item_id){
                if (queue_name === 'user'){
                    queue_name = 'created_by';
                }
                if (item_type === 'managed_storages'){
                    item_type += '/in_storage';
                    switch (queue_name) {
                        case 'folder':
                        case 'tag':
                            queue_name = queue_name + 's';
                            break;
                        case 'category':
                            queue_name = 'categories';
                            break;
                    }
                }

                window.location = encodeURI("/" + this.current_item_type + "/" + item_type + '?queue='+queue_name + '&id='+item_id);
            },
            itemVisible (item) {
                let currentName = item.name.toLowerCase();
                let currentInput = this.inputValue.toLowerCase();
                return currentName.includes(currentInput);
            },
            async getList() {
                this.grouped_itemList = {
                    loading: [
                        {
                            id: -1,
                            name: 'Loading...',
                            type: 'loading'
                        }
                    ]
                };
                if (this.inputValue !== '') {

                    let params = {
                        keyword: this.inputValue,
                        current_item_type: this.current_item_type
                    };

                    const { data } = await searchResource.list(params, null, false, 'post');

                    if (data.hasOwnProperty('meta')){
                        this.itemList = data.data;
                        this.itemMeta = data.meta;
                        if (this.itemList){
                            this.grouped_itemList = this.itemList.reduce((r, a) => {
                                r[a.type] = [...r[a.type] || [], a];
                                return r;
                            }, {});
                        }
                        if (this.itemMeta){
                            for (const itemMetaKey in this.itemMeta) {
                                if (!this.grouped_itemList.hasOwnProperty(itemMetaKey)){
                                    this.grouped_itemList[itemMetaKey] = [];
                                }
                            }
                        }
                    }else{
                        // console.log('cancelled');
                    }
                }
                this.apiLoaded = true;
            }
        },
        watch:{
            inputValue(value){
                this.getList();
            }
        }
    }
</script>
